/* Column profiles and drift over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * **Two reads and no source access.** `GET /sketches` returns what a past scan measured and
 * `GET /sketch-drift` compares two stored sketches — the response says so itself, in
 * `computed_from`. That property is what makes drift cheap and it is not obvious from a payload of
 * numbers, so the screen renders the platform's own sentence rather than restating it.
 *
 * **`POST /profile` *is* called from here, and the cost is why it is guarded rather than absent.**
 * This paragraph used to say it was not called at all — true when written, and untrue since the
 * panel and `DatasetDetail` grew the action. What has not changed is the cost S-25 records: a
 * profile is 20 full `GROUP BY` scans of a customer's table, nothing server-side bounds concurrent
 * ones, and a double-clicked button buys 40. So every caller disables while pending, and the bulk
 * runner in `catalog-browser` bounds its own concurrency and skips datasets already profiled — see
 * `requestProfile`, which is the one implementation all of them share.
 */

import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  isColumnDrift,
  isColumnSketch,
  isDatasetSketches,
  isProfileComparison,
  isProfileComparisonSummary,
  isProfileHistory,
  isProfileRunSummary,
  isSketchDrift,
} from '@/shared/api/generated/catalog';
import type {
  DatasetSketches,
  ProfileComparison,
  ProfileHistory,
  SketchDrift,
} from '@/shared/api/generated/catalog';
import { forgetIdempotencyKey, getOne, idempotencyKeyFor, post } from '@/shared/api/http';

export interface ProfileRequestResult {
  readonly dataset_id: string;
  readonly dataset_version_id: string;
  readonly columns: readonly string[];
  readonly column_limit: number;
  readonly truncated: boolean;
}

function isProfileRequestResult(value: unknown): value is ProfileRequestResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['dataset_version_id'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    typeof candidate['column_limit'] === 'number' &&
    typeof candidate['truncated'] === 'boolean'
  );
}

/** One `POST /profile`, as a plain function.
 *
 * **Extracted so the bulk runner and this hook are one implementation.** A hook is bound to one
 * `datasetId` and cannot be called in a loop, so a "run for every dataset" control had the choice
 * of re-writing this request or of calling out to it. Re-writing it is the shape that drifts: the
 * idempotency scope, the guard, and the `finally` that clears the key are three details a second
 * copy gets right on the day it is written and not afterwards.
 *
 * The idempotency key is scoped `catalog.profile` per dataset and cleared in a `finally`, exactly
 * as before — so a retry of a *failed* request reuses the key while a fresh press after a success
 * mints a new one. Nothing about the individual flow changed: `useRequestProfile` below is the same
 * mutation with the same key, the same guard and the same invalidations.
 */
export async function requestProfile(datasetId: string): Promise<ProfileRequestResult> {
  const key = idempotencyKeyFor('catalog.profile', datasetId);
  try {
    return await post(
      `/v1/catalog/datasets/${datasetId}/profile`,
      key,
      isProfileRequestResult,
      'profile request',
    );
  } finally {
    forgetIdempotencyKey('catalog.profile', datasetId);
  }
}

/** The mutation key every profile request for one dataset shares, so a progress readout can follow a
 * run whichever button started it. */
export function profileMutationKey(datasetId: string): readonly unknown[] {
  return ['catalog', 'profile', datasetId];
}

export function useRequestProfile(datasetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: profileMutationKey(datasetId),
    mutationFn: () => requestProfile(datasetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'sketches', datasetId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'sketch-drift', datasetId],
      });
      /* A new run joins the history and can become the newest side of a comparison. */
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'profile-history', datasetId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'profile-comparison', datasetId],
      });
    },
  });
}

function isSketchesWithColumns(value: unknown): value is DatasetSketches {
  if (!isDatasetSketches(value)) return false;
  return value.columns.every(isColumnSketch);
}

function isDriftWithColumns(value: unknown): value is SketchDrift {
  if (!isSketchDrift(value)) return false;
  return value.columns.every(isColumnDrift);
}

/** The current version's stored sketches, or `null` when the dataset was never profiled.
 *
 * A 404 is a legitimate answer here rather than an error: "never profiled" is the normal state of a
 * freshly harvested dataset, and surfacing it as a failure would put a red alert on every new table.
 * The panel renders it as an invitation instead.
 */
export const PROFILE_POLL_MS = 2000;
/** How often the panel re-reads while a profile is running.
 *
 * Two seconds because a column's `GROUP BY` on a real table takes seconds, so a faster poll would
 * mostly return the same count — and this read is against Lumiq's own database rather than the
 * customer's, so the cost is a cheap indexed count either way. Polling **stops** the moment the
 * count reaches the expected column total, so a finished profile costs nothing.
 */

export function useSketches(
  datasetId: string,
  /** How many columns a complete profile would have, or `undefined` if the caller does not know.
   *
   * **The poll is bounded by this rather than by a timer.** `undefined` means no polling at all,
   * which is the right default: a caller that cannot say what "finished" looks like must not start
   * an interval that never ends. */
  expectedColumns?: number,
): UseQueryResult<DatasetSketches | null> {
  return useQuery({
    queryKey: ['catalog', 'sketches', datasetId],
    /* **Polls only while the count is genuinely short of the total.** `capture` appends and commits
       one column at a time, so this count really does rise as scans complete — which is what makes
       a progress reading a measurement rather than an animation. The interval returns `false` once
       the profile is complete, so React Query stops on its own without anybody clearing a timer. */
    refetchInterval: (query) => {
      if (expectedColumns === undefined || expectedColumns <= 0) return false;
      const stored = query.state.data?.columns.length ?? 0;
      return stored >= expectedColumns ? false : PROFILE_POLL_MS;
    },
    queryFn: () => fetchSketches(datasetId),
  });
}

/* The two reads as plain functions, so the single-dataset hooks and the estate view's fan-out are one
 * implementation under one cache key — a table opened from the Profiles screen arrives with its
 * profile already read, and the two cannot disagree about what a 404 means. */
async function fetchSketches(datasetId: string): Promise<DatasetSketches | null> {
  try {
    return await getOne(
      `/v1/catalog/datasets/${datasetId}/sketches`,
      isSketchesWithColumns,
      'dataset sketches',
    );
  } catch (failure) {
    if (failure instanceof Error && 'status' in failure && failure.status === 404) {
      return null;
    }
    throw failure;
  }
}

async function fetchSketchDrift(datasetId: string): Promise<SketchDrift | null> {
  try {
    return await getOne(
      `/v1/catalog/datasets/${datasetId}/sketch-drift`,
      isDriftWithColumns,
      'sketch drift',
    );
  } catch (failure) {
    if (failure instanceof Error && 'status' in failure && failure.status === 404) {
      return null;
    }
    throw failure;
  }
}

/** Drift against the previous profiled version, or `null` when there is nothing to compare with. */
export function useSketchDrift(datasetId: string): UseQueryResult<SketchDrift | null> {
  return useQuery({
    queryKey: ['catalog', 'sketch-drift', datasetId],
    queryFn: () => fetchSketchDrift(datasetId),
  });
}

/** Every listed table's profile and drift, for the estate view.
 *
 * **Only for tables that have been profiled**, which the caller decides from the facts: asking for a
 * never-profiled table's sketches is a request whose answer is already known to be a 404.
 *
 * **A map entry is present only once its read has answered.** An absent entry is "not read yet" and a
 * `null` is "read, and there is none" — the two states a card must not confuse, since one is a
 * spinner and the other is an invitation to profile. A failed read stays absent and is counted in
 * `failed`, so the screen can say some profiles could not be read rather than drawing them empty.
 */
export interface EstateProfiles {
  readonly sketches: ReadonlyMap<string, DatasetSketches | null>;
  readonly drift: ReadonlyMap<string, SketchDrift | null>;
  /** Reads still in flight. */
  readonly pending: number;
  /** Reads that failed. */
  readonly failed: number;
}

/** How long an estate read stays fresh. The screen fans out two reads per table, so returning to it
 * within a minute should not repeat them all. */
const ESTATE_STALE_MS = 60_000;

export function useEstateProfiles(datasetIds: readonly string[]): EstateProfiles {
  return useQueries({
    queries: datasetIds.flatMap((id) => [
      {
        queryKey: ['catalog', 'sketches', id],
        queryFn: () => fetchSketches(id),
        staleTime: ESTATE_STALE_MS,
        retry: false,
      },
      {
        queryKey: ['catalog', 'sketch-drift', id],
        queryFn: () => fetchSketchDrift(id),
        staleTime: ESTATE_STALE_MS,
        retry: false,
      },
    ]),
    combine: (results) => {
      const sketches = new Map<string, DatasetSketches | null>();
      const drift = new Map<string, SketchDrift | null>();
      let pending = 0;
      let failed = 0;
      results.forEach((result, index) => {
        const id = datasetIds[Math.floor(index / 2)];
        if (id === undefined) return;
        if (result.isPending) pending += 1;
        if (result.isError) failed += 1;
        if (!result.isSuccess) return;
        if (index % 2 === 0) sketches.set(id, result.data as DatasetSketches | null);
        else drift.set(id, result.data as SketchDrift | null);
      });
      return { sketches, drift, pending, failed };
    },
  });
}

/** The dataset's profiling runs, newest first.
 *
 * **An empty list is an answer, not an error**: a freshly harvested table has never been profiled, and
 * the server says so with a 200 rather than a 404. A 404 here means the dataset itself is not this
 * tenant's, which is a failure worth showing.
 */
/** How many runs the history lists by default, and the most the server will list. */
export const HISTORY_RUNS = 20;
export const MAX_HISTORY_RUNS = 50;

/** The dataset's profiling runs, newest first, at most `limit` of them.
 *
 * The previous answer stays on screen while a longer list loads, so asking for more runs does not
 * blank the strip a reader was looking at. */
export function useProfileHistory(
  datasetId: string,
  limit: number = HISTORY_RUNS,
): UseQueryResult<ProfileHistory> {
  return useQuery({
    queryKey: ['catalog', 'profile-history', datasetId, limit],
    placeholderData: keepPreviousData,
    queryFn: () =>
      getOne(
        `/v1/catalog/datasets/${datasetId}/profile-history?limit=${String(limit)}`,
        isProfileHistory,
        'profile history',
      ),
  });
}

/* The generated guard checks that `baseline`, `current` and `summary` are present and not what they
 * hold — nested objects are presence-checked only. Every figure on the comparison screen is read from
 * those three, so a renamed field inside one would otherwise render as blank cells under a heading
 * that claims a comparison. */
function isWholeComparison(value: unknown): value is ProfileComparison {
  return (
    isProfileComparison(value) &&
    isProfileRunSummary(value.baseline) &&
    isProfileRunSummary(value.current) &&
    isProfileComparisonSummary(value.summary)
  );
}

/** Two runs compared, or `null` when there is nothing to compare.
 *
 * Either id may be absent: no later run means the newest, and no earlier run means the one before the
 * later run. **A 404 is a legitimate answer** — a dataset with one run, or a later run that is the
 * oldest — and reads as `null` rather than as a failure, for `useSketchDrift`'s reason. The comparison
 * is read from stored profiles only; asking for one never touches the data source.
 *
 * `enabled` lets the caller hold the request back while the choice is one the server would refuse,
 * such as the same run on both sides, so the screen says why rather than showing a 422.
 */
export function useProfileComparison(
  datasetId: string,
  baselineRunId: string | null,
  currentRunId: string | null,
  enabled: boolean,
): UseQueryResult<ProfileComparison | null> {
  return useQuery({
    queryKey: ['catalog', 'profile-comparison', datasetId, baselineRunId, currentRunId],
    enabled,
    /* The comparison a reader was looking at stays on screen, dimmed, while the next pair loads —
       choosing a different run should read as the figures changing, not as the page emptying. */
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const query = new URLSearchParams();
      if (baselineRunId !== null) query.set('baseline_run_id', baselineRunId);
      if (currentRunId !== null) query.set('current_run_id', currentRunId);
      const encoded = query.toString();
      const suffix = encoded === '' ? '' : `?${encoded}`;
      try {
        return await getOne(
          `/v1/catalog/datasets/${datasetId}/profile-comparison${suffix}`,
          isWholeComparison,
          'profile comparison',
        );
      } catch (failure) {
        if (failure instanceof Error && 'status' in failure && failure.status === 404) {
          return null;
        }
        throw failure;
      }
    },
  });
}
